import * as THREE from 'three'

import { createFireEffect } from './fireEffect'
import { createSparkleEffect } from './sparkleEffect'
import { MATERIAL_TILE_STUDS, createMaterialTexture } from './materialTextures'
import {
  GLOW_MAX_SATURATION,
  GLOW_SHELL_LAYERS,
  MATERIAL_SPECS,
  applyTopPatternRepeat,
} from './materialSpecs'
import { MeshEntry, composeOrientation } from './meshEntry'

import { ObjectRuntimeState, ShapeKind } from '../types'

/** 球・円柱の丸い形状か（角張った box / wedge / cornerWedge と区別する）。 */
const isRoundShape = (shape: ShapeKind): boolean =>
  shape === 'ball' || shape === 'cylinder'

/**
 * Neon の多層グローシェル（光膜）を付けるべきか。
 *
 * box 等の角形は原色 Neon だと外周が「透明な枠」に見えるため白系（低彩度）に
 * 限る。一方 球/円柱 の丸い形状は原色でも光膜が柔らかい光輪になり実機の Neon
 * らしく光るので、彩度に関わらず許可する（教材「ボールをネオンにすると光り輝く」）。
 * ただし丸形状の彩度バイパスは Neon 専用（allowsSaturatedRoundGlow）。ForceField も
 * emissiveFollowsColor=true だが半透明シマーのため原色球には従来どおり白系のみ許可する。
 * 外部モデル・デカール付き・ほぼ透明（見えない壁の白光防止）は対象外。
 */
export const isGlowEligible = (params: {
  emissiveFollowsColor: boolean
  allowsSaturatedRoundGlow: boolean
  hasModel: boolean
  hasDecals: boolean
  glowAlpha: number
  shape: ShapeKind
  glowSaturation: number
}): boolean =>
  params.emissiveFollowsColor &&
  params.glowAlpha > 0.01 &&
  !params.hasModel &&
  !params.hasDecals &&
  ((isRoundShape(params.shape) && params.allowsSaturatedRoundGlow) ||
    params.glowSaturation < GLOW_MAX_SATURATION)

/**
 * 1 マテリアルに状態（color/transparency）を反映する（GLB の任意マテリアル用）。
 * - color は `color: Color` プロパティを持つ材質（MeshStandardMaterial 等）でのみ更新。
 * - transparent/opacity/depthWrite は base Material 共通プロパティで安全に更新。
 * - 透明オブジェクトは depthWrite=false にして自身による奥行きソート崩れを抑える。
 */
// applyMaterialState 用の再利用テンポラリ（state.color の正規化比較に使う）。
const _stateColor = new THREE.Color()

const applyMaterialState = (
  material: THREE.Material,
  state: ObjectRuntimeState,
): void => {
  // Roblox はテクスチャ付き MeshPart / SpecialMesh に Part.Color を乗算しない
  // （テクスチャがそのまま表示される）。テクスチャ読込側が立てるフラグを見て
  // 乗算をスキップしないと、灰色 Part.Color の木・岩がテクスチャごと黒ずむ。
  // ただし学習者がプログラムで色を明示変更したとき（テクスチャ読込時に退避
  // した初期色から変化）は乗算して反映する（例: RJF11 の UFO 円盤の色変え）。
  if ('color' in material && material.color instanceof THREE.Color) {
    if (material.userData.textureOverridesColor !== true) {
      material.color.set(state.color)
    } else {
      const initialColor = material.userData.initialColor as string | undefined
      _stateColor.set(state.color)
      if (
        initialColor !== undefined &&
        _stateColor.getHexString() !== initialColor
      ) {
        material.color.copy(_stateColor)
      } else {
        material.color.setHex(0xffffff)
      }
    }
  }
  const opacity = 1 - state.transparency
  const transparent = state.transparency > 0
  material.transparent = transparent
  material.opacity = opacity
  material.depthWrite = !transparent
  material.needsUpdate = true
}

/**
 * Neon/ForceField（emissiveFollowsColor）の自己発光を反映する。albedo を黒へ
 * 落として emissive だけで発色させ、トーンマッピングも外す。NeutralToneMapping
 * は輝度の高い純色を脱彩度するため、通すと純青のワープブロックが紫に濁って
 * しまう（実機は青く光る）。ライトの鏡面反射も色を濁すので roughness を最大にする。
 */
const applyNeonEmissive = (
  material: THREE.MeshStandardMaterial,
  state: ObjectRuntimeState,
): void => {
  applyMaterialState(material, state)
  if (state.transparency > 0.01) {
    // 半透明 Neon（例: RJF08 の色付きカベ trans=0.5）は albedo を黒にすると
    // 「暗い膜」に見えてしまう（合成で背景と混ざる比率が大きく、emissive の
    // 寄与が薄まるため。実機は明るい半透明色）。albedo に色を残し、emissive
    // と合わせて明るさを保つ（従来方式）。
    material.emissive.set(state.color)
    material.emissiveIntensity = 1
    return
  }
  material.color.setHex(0x000000)
  material.emissive.set(state.color)
  material.emissiveIntensity = 1
  material.toneMapped = false
  material.roughness = 1
}

/**
 * GLB/メッシュ authored material の発光関連プロパティを記憶・復元する。
 * applyNeonEmissive は authored material を破壊的に書き換えるため、
 * 「素材をかえる」で Neon から通常素材へ戻したときに元の質感へ復元できる
 * よう、初回適用前のベース値を userData に退避しておく。
 */
interface PreviewBaseStandardState {
  emissive: THREE.Color
  emissiveIntensity: number
  toneMapped: boolean
  roughness: number
}

const rememberBaseStandardState = (
  material: THREE.MeshStandardMaterial,
): void => {
  material.userData.previewBase ??= {
    emissive: material.emissive.clone(),
    emissiveIntensity: material.emissiveIntensity,
    toneMapped: material.toneMapped,
    roughness: material.roughness,
  } satisfies PreviewBaseStandardState
}

const restoreBaseStandardState = (
  material: THREE.MeshStandardMaterial,
): void => {
  const base = material.userData.previewBase as
    | PreviewBaseStandardState
    | undefined
  if (!base) {
    return
  }
  material.emissive.copy(base.emissive)
  material.emissiveIntensity = base.emissiveIntensity
  material.toneMapped = base.toneMapped
  material.roughness = base.roughness
}

/**
 * 箱の 1 面マテリアル（MeshStandardMaterial 既定）に Roblox 質感まで含めて反映する。
 * color/transparency に加え、roughness/metalness/emissive を spec から設定する。
 * Neon は emissive を state.color に追従させ、それ以外は無発光。
 */
const applyStandardMaterialState = (
  material: THREE.MeshStandardMaterial,
  state: ObjectRuntimeState,
): void => {
  applyMaterialState(material, state)
  const spec = MATERIAL_SPECS[state.material]
  material.roughness = spec.roughness
  material.metalness = spec.metalness
  // 「素材をかえる」で Neon から通常素材へ戻れるよう、毎回既定へ戻してから
  // emissiveFollowsColor 分岐で上書きする。
  material.toneMapped = true
  if (spec.emissiveFollowsColor) {
    applyNeonEmissive(material, state)
  } else if (spec.emissiveHex !== undefined) {
    // 固定色の弱い発光（CrackedLava の溶岩光など）。
    material.emissive.setHex(spec.emissiveHex)
    material.emissiveIntensity = 0.4
  } else {
    material.emissive.setHex(0x000000)
    material.emissiveIntensity = 0
  }
}

/**
 * モデル配下の Mesh/Points/Line のマテリアル（配列含む）を 1 度だけ収集する。
 * 読込直後に Set へ詰めることで、毎フレームの traverse を避ける。
 */
export const collectModelMaterials = (
  model: THREE.Object3D,
  out: Set<THREE.Material>,
): void => {
  model.traverse((child) => {
    if (
      child instanceof THREE.Mesh ||
      child instanceof THREE.Points ||
      child instanceof THREE.Line
    ) {
      const mats =
        Array.isArray(child.material) ? child.material : [child.material]
      for (const mat of mats) {
        out.add(mat)
      }
    }
  })
}

/** 実行時状態をエントリへ反映する（three 側は性質上 mutate）。 */
export const applyState = (
  entry: MeshEntry,
  state: ObjectRuntimeState,
): void => {
  entry.container.position.set(
    state.position.x,
    state.position.y,
    state.position.z,
  )
  // 最終姿勢 = Y ヨー(rotationY) ∘ 基準向き(orientation)。傾いた壁・ランプの
  // X/Z 姿勢を保ちつつ、子の turnRight 等による Y ヨーも合成する。
  composeOrientation(
    entry.container.quaternion,
    state.rotationY,
    state.orientation,
  )
  entry.container.scale.set(state.size.x, state.size.y, state.size.z)
  // 球・円柱は断面を真円に保つ。container.scale=size の契約は崩さず、子メッシュ側で
  // 最小寸法に揃う逆スケールを掛ける（物理コライダー側も min 寸法で一致させている）。
  if (entry.shape === 'ball') {
    const d = Math.min(state.size.x, state.size.y, state.size.z)
    entry.box.scale.set(d / state.size.x, d / state.size.y, d / state.size.z)
  } else if (entry.shape === 'cylinder') {
    // 軸に直交する2辺を直径として真円に保つ（軸方向は長さなので等倍）。
    if (entry.cylinderAxis === 'y') {
      const d = Math.min(state.size.x, state.size.z)
      entry.box.scale.set(d / state.size.x, 1, d / state.size.z)
    } else {
      const d = Math.min(state.size.y, state.size.z)
      entry.box.scale.set(1, d / state.size.y, d / state.size.z)
    }
  }
  entry.container.visible = state.visible

  // 炎エフェクト（Roblox Fire 相当）を fire フラグに追従して付け外しする。
  // container は size でスケールされるため、エフェクトは単位空間で生成される。
  if (state.fire && !entry.fireEffect) {
    const effect = createFireEffect()
    entry.fireEffect = effect
    entry.container.add(effect.group)
  } else if (!state.fire && entry.fireEffect) {
    entry.container.remove(entry.fireEffect.group)
    entry.fireEffect.dispose()
    entry.fireEffect = undefined
  }

  // キラキラエフェクト（Roblox Sparkles 相当）も fire と同様に付け外しする。
  // パーティクルはワールド studs 単位で動くため（薄い板でも潰れず立ち上る）、
  // 発生範囲に Part 寸法を渡し、container.scale=size を group の逆スケールで
  // 打ち消す。寸法が変わったら発生範囲も追従させるため作り直す（setSizeTo 対応）。
  const sparkleSizeKey =
    state.sparkle ?
      `${state.size.x}\x00${state.size.y}\x00${state.size.z}`
    : undefined
  if (entry.sparkleEffect && entry.sparkleSizeKey !== sparkleSizeKey) {
    entry.container.remove(entry.sparkleEffect.group)
    entry.sparkleEffect.dispose()
    entry.sparkleEffect = undefined
  }
  if (state.sparkle && !entry.sparkleEffect) {
    const effect = createSparkleEffect(state.size)
    effect.group.scale.set(
      1 / Math.max(1e-6, state.size.x),
      1 / Math.max(1e-6, state.size.y),
      1 / Math.max(1e-6, state.size.z),
    )
    entry.sparkleEffect = effect
    entry.container.add(effect.group)
  }
  entry.sparkleSizeKey = sparkleSizeKey

  // size(x,z) が変わった時だけスタッド repeat を更新する（毎フレーム不要）。
  if (entry.studTexture) {
    const sizeKey = `${state.size.x}\x00${state.size.z}`
    if (entry.sizeKey !== sizeKey) {
      entry.sizeKey = sizeKey
      // size.x/z（studs/unit）に比例してスタッド数を増やす。負/0 は World 側で
      // クランプ済みである前提だが、Math.max で防御し texture を不正にしない。
      entry.studTexture.repeat.set(
        Math.max(0.01, state.size.x),
        Math.max(0.01, state.size.z),
      )
      entry.studTexture.needsUpdate = true
      if (entry.topPatternTexture) {
        applyTopPatternRepeat(entry.topPatternTexture.texture, state)
      }
    }
  }

  // color/transparency/material が前回と同値ならマテリアル反映と needsUpdate を短絡し、
  // 不要なシェーダ再評価を避ける（World は頻繁に notify するため重要）。
  const visualKey = `${state.color}\x00${state.transparency}\x00${state.material}`
  if (entry.visualKey === visualKey) {
    return
  }
  entry.visualKey = visualKey
  // 「素材をかえる」を見た目で伝えるため、マテリアルごとの模様テクスチャを
  // albedo マップへ載せ替える（白ベース模様 × Part 色の乗算で着色される）。
  if (entry.materialTexture?.material !== state.material) {
    entry.materialTexture?.texture.dispose()
    const texture = createMaterialTexture(state.material)
    if (texture) {
      // 模様 1 タイル = MATERIAL_TILE_STUDS 角として面サイズに比例して繰り返す。
      texture.repeat.set(
        Math.max(0.5, (state.size.x + state.size.z) / 2 / MATERIAL_TILE_STUDS),
        Math.max(0.5, state.size.y / MATERIAL_TILE_STUDS),
      )
      entry.materialTexture = { material: state.material, texture }
    } else {
      entry.materialTexture = undefined
    }
  }
  // 模様マテリアル（Brick / Grass 等）は天面もスタッドでなく模様にする。実機の
  // 天面に LEGO 風スタッドは無く、バッチ済み静的 box（全面模様シェーダ）とも
  // 見た目を揃えるため。repeat が天面サイズ（x/z）基準で側面用と異なるため、
  // 専用テクスチャを別途持つ。無地マテリアルは従来どおり天面スタッドのまま。
  if (entry.studTexture) {
    if (entry.topPatternTexture?.material !== state.material) {
      entry.topPatternTexture?.texture.dispose()
      const texture = createMaterialTexture(state.material)
      if (texture) {
        applyTopPatternRepeat(texture, state)
        entry.topPatternTexture = { material: state.material, texture }
      } else {
        entry.topPatternTexture = undefined
      }
    }
  }
  const patternTexture = entry.materialTexture?.texture ?? null
  for (const material of entry.boxMaterials) {
    applyStandardMaterialState(material, state)
    if (material === entry.topMaterial && entry.studTexture) {
      // スタッド付き天面: 模様マテリアルなら模様、無地ならスタッドを貼る。
      const desired = entry.topPatternTexture?.texture ?? entry.studTexture
      if (material.map !== desired) {
        material.map = desired
        material.needsUpdate = true
      }
    } else if (material.map !== patternTexture) {
      material.map = patternTexture
      material.needsUpdate = true
    }
  }
  // GLB authored material には spec を強制しない（色/不透明度のみ反映）。
  // ただし Neon/ForceField（emissiveFollowsColor）はメッシュ描画でも自己発光が
  // 支配する素材なので、box 面と同じ発光・toneMapped 処理を適用して色を揃える
  // （テクスチャがある場合はテクスチャ表現を優先して通常反映のまま）。
  // applyNeonEmissive は authored material を破壊的に変えるため、適用前に
  // ベース値を退避し、「素材をかえる」で通常素材へ戻ったときは復元する。
  const spec = MATERIAL_SPECS[state.material]
  for (const material of entry.modelMaterials) {
    if (material instanceof THREE.MeshStandardMaterial) {
      rememberBaseStandardState(material)
      if (
        spec.emissiveFollowsColor &&
        material.userData.textureOverridesColor !== true
      ) {
        applyNeonEmissive(material, state)
      } else {
        restoreBaseStandardState(material)
        applyMaterialState(material, state)
      }
    } else {
      applyMaterialState(material, state)
    }
  }

  // Neon の発光の滲み（多層グローシェル）。bloom 後処理を持たないため、本体
  // より一回り大きい加算合成の光膜を外側ほど薄く重ね、「光っている」印象を
  // 出す（例: RJF08 のゴール鳥居）。対象は外部モデルなし・デカールなし
  // （例: RJF08 の SpawnLocation は Neon 素材の台で、光膜は印象を崩す）。
  // box の子に置くことで、球/円柱の断面補正スケールにも追従する。
  const glowColor = new THREE.Color(state.color)
  const glowSaturation =
    Math.max(glowColor.r, glowColor.g, glowColor.b) -
    Math.min(glowColor.r, glowColor.g, glowColor.b)
  // 透明度はグローにも反映する。完全透明の Neon（例: RJF08 の見えない
  // トリガー Collision）に光膜を出すと「見えない壁が白く光る」ため除外。
  const glowAlpha = 1 - state.transparency
  const wantsGlow = isGlowEligible({
    emissiveFollowsColor: spec.emissiveFollowsColor,
    allowsSaturatedRoundGlow: spec.saturatedRoundGlow === true,
    hasModel: !!entry.model,
    hasDecals: entry.hasDecals === true,
    glowAlpha,
    shape: entry.shape,
    glowSaturation,
  })
  if (wantsGlow && !entry.glowGroup) {
    const group = new THREE.Group()
    const materials: THREE.MeshBasicMaterial[] = []
    for (const layer of GLOW_SHELL_LAYERS) {
      const material = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: layer.opacity,
        blending: THREE.AdditiveBlending,
        side: THREE.BackSide,
        depthWrite: false,
        toneMapped: false,
      })
      const mesh = new THREE.Mesh(entry.boxGeometry, material)
      mesh.scale.setScalar(layer.scale)
      // クリック判定・遮蔽フェードのレイキャスト対象にしない。
      mesh.raycast = () => undefined
      group.add(mesh)
      materials.push(material)
    }
    entry.glowGroup = group
    entry.glowMaterials = materials
    entry.box.add(group)
  } else if (!wantsGlow && entry.glowGroup) {
    entry.box.remove(entry.glowGroup)
    for (const material of entry.glowMaterials ?? []) {
      material.dispose()
    }
    entry.glowGroup = undefined
    entry.glowMaterials = undefined
  }
  entry.glowMaterials?.forEach((material, index) => {
    material.color.set(state.color)
    material.opacity = (GLOW_SHELL_LAYERS[index]?.opacity ?? 0) * glowAlpha
  })
}
